import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useEngineState } from '../../club/ClubProvider.tsx'
import { Banner, PendingBanner } from '../../ui/components.tsx'
import { useWriteAccess } from '../../ui/hooks.ts'

/** The cosmetic write gate plus the network: every set-up command is online (the server's verdict is what the screen shows), so a form saves only when both hold. */
export function useSetupAccess(permission: string) {
  const access = useWriteAccess(permission)
  const online = useEngineState().online !== false
  return { ...access, online, canSave: access.allowed && online }
}

/** The result of the last action, where the user is looking. The test ids are the page's: only one tab is mounted at a time. */
export function Feedback({ error, ok, pending }: { error?: string | null; ok?: string | null; pending?: boolean }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => { if (error || ok || pending) ref.current?.scrollIntoView?.({ block: 'nearest' }) }, [error, ok, pending])
  if (!error && !ok && !pending) return null
  return (
    <div ref={ref} className="flex flex-col gap-2">
      {pending && <PendingBanner testId="setup-pending" />}
      {error && <Banner tone="error"><span data-testid="setup-error">{error}</span></Banner>}
      {ok && <Banner tone="ok"><span data-testid="setup-ok">{ok}</span></Banner>}
    </div>
  )
}

/** Why Save is still grey: the checks the server will make again, said early. */
export function Problems({ codes }: { codes: readonly string[] }) {
  const { t } = useTranslation()
  if (codes.length === 0) return null
  return (
    <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" data-testid="setup-problems">
      <p className="font-semibold">{t('planSetup.toFix')}</p>
      <ul className="ml-5 list-disc">{codes.map((c) => <li key={c} data-code={c}>{t(`planSetup.err.${c}`)}</li>)}</ul>
    </div>
  )
}

/** A button that asks once ("are you sure?") before it acts. `id` is the test id of the first button; the others are `<id>-confirm` and `<id>-cancel`. */
export function ConfirmAction({ id, label, message, confirmLabel, busy, disabled, danger, onConfirm }: {
  id: string; label: string; message: string; confirmLabel: string; busy: boolean; disabled?: boolean; danger?: boolean; onConfirm: () => Promise<unknown> | void
}) {
  const { t } = useTranslation()
  const [asking, setAsking] = useState(false)
  if (!asking) return <button type="button" className="btn !min-h-11" disabled={disabled} data-testid={id} onClick={() => setAsking(true)}>{label}</button>
  return (
    <div className="flex flex-col gap-2 rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-amber-900" data-testid={`${id}-ask`}>
      <p className="text-sm font-medium">{message}</p>
      <div className="flex gap-2">
        <button type="button" className={`btn !min-h-11 flex-1 ${danger ? 'btn-danger' : 'btn-primary'}`} disabled={busy} data-testid={`${id}-confirm`}
          onClick={() => { void Promise.resolve(onConfirm()).finally(() => setAsking(false)) }}>{confirmLabel}</button>
        <button type="button" className="btn !min-h-11 flex-1" disabled={busy} data-testid={`${id}-cancel`} onClick={() => setAsking(false)}>{t('common.cancel')}</button>
      </div>
    </div>
  )
}

/** A pressed / not pressed chip (weekdays, month days): at least 44 px square. */
export function ToggleChip({ on, onClick, children, label, testId, disabled, className = '' }: { on: boolean; onClick: () => void; children: ReactNode; label?: string; testId?: string; disabled?: boolean; className?: string }) {
  return (
    <button type="button" aria-pressed={on} aria-label={label} disabled={disabled} data-testid={testId} onClick={onClick}
      className={`min-h-11 min-w-11 rounded-xl border-2 px-2 text-sm font-semibold disabled:opacity-50 ${className} ${on ? 'border-brand bg-brand text-white dark:border-emerald-600 dark:bg-emerald-700' : 'border-slate-400 bg-white text-slate-900 dark:border-slate-500 dark:bg-slate-800 dark:text-white'}`}>
      {children}
    </button>
  )
}

/** A tick-box row with a 44 px touch target. */
export function CheckRow({ checked, onChange, children, testId, disabled, data }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; testId?: string; disabled?: boolean; data?: Record<string, string> }) {
  const attrs = Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [`data-${k}`, v]))
  return (
    <label className="flex min-h-11 items-center gap-3 rounded-xl border-2 border-slate-300 px-3 py-1 font-medium dark:border-slate-600">
      <input type="checkbox" className="size-5 shrink-0" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testId} {...attrs} />
      <span className="min-w-0">{children}</span>
    </label>
  )
}

/** Digits only, for the whole-number fields typed on a phone. */
export const digits = (s: string): string => s.replace(/\D/g, '')
